import { useState, useEffect, useCallback, useRef } from "react";
import { getProjectsUnified, createProject, updateProject, deleteProject, getProjectDeletionImpact, getClients, getVenues, getClientContacts, extractErrorMessage } from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
  ErrorState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { ProjectNameAutocomplete } from "../components/shared/ProjectNameAutocomplete";
import { ClientSelect } from "../components/shared/ClientSelect";
// EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1
import { QuickAddVenueDialog } from "../components/shared/QuickAddVenueDialog";
// EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
import { filterVenuesForClient } from "../lib/venueFilters";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DatePicker } from "../components/ui/date-picker";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, FolderKanban, Pencil, Trash2, Eye, Loader2, Search, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTableDisplaySettings, getZoomClasses } from "../hooks/useZoom";
import { ZoomControl } from "../components/common/ZoomControl";
import PageZoomContainer from "../components/layout/PageZoomContainer";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";
// EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
import { formatProjectClient } from "../lib/projectClient";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useFormatters();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [venues, setVenues] = useState([]);
  // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Client contacts state
  const [clientContacts, setClientContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1
  // "+ Add Venue" quick-add flow next to the Default Venue selector.
  const [quickAddVenueOpen, setQuickAddVenueOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Governed deletion state
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");
  
  // EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Server-side query state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Debounce timer for search
  const searchTimeoutRef = useRef(null);
  
  // EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom and wrap settings
  const { 
    currentZoom, scale, zoomIn, zoomOut, canZoomIn, canZoomOut,
    wrapText, toggleWrap 
  } = useTableDisplaySettings("projects-page");
  const zoomClasses = getZoomClasses(scale);
  
  // Form validation state
  const [dateError, setDateError] = useState("");
  
  const [formData, setFormData] = useState({
    client_id: "",
    // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Client contact for quotes/invoices
    client_contact_id: "",
    name: "",
    description: "",
    status: "draft",
    start_date: "",
    end_date: "",
    // New fields
    project_type: "order",
    quote_ref: "",
    po_number: "",
    cost_centre: "none",
    cost: "",
    default_venue_id: "",
    comments: "",
    enable_gps_sign_in: false,
  });

  useEffect(() => {
    loadReferenceData();
  }, []);

  // EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Re-fetch when query params change
  useEffect(() => {
    loadProjects();
  }, [page, sortField, sortDirection]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      // Reset to page 1 when search changes
      if (page !== 1) {
        setPage(1);
      } else {
        loadProjects();
      }
    }, 300); // 300ms debounce
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Load reference data (clients, venues) - only once
  const loadReferenceData = async () => {
    try {
      const [clientRes, venuesRes] = await Promise.all([
        getClients(),
        getVenues(),
      ]);
      setClients(clientRes.data);
      setVenues(venuesRes.data);
    } catch (err) {
      console.error("Failed to load reference data:", err);
    }
  };

  // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Load client contacts when client changes
  const loadClientContacts = useCallback(async (clientId) => {
    if (!clientId) {
      setClientContacts([]);
      return;
    }
    
    try {
      setContactsLoading(true);
      const response = await getClientContacts(clientId);
      setClientContacts(response.data || []);
    } catch (err) {
      console.error("Failed to load client contacts:", err);
      setClientContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Handle client change
  const handleClientChange = useCallback((value) => {
    // EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
    // When the client changes, any currently selected default venue
    // that is not visible to the new client must be cleared so the
    // form doesn't submit a venue the new client can't see.
    setFormData(prev => {
      const stillVisible =
        !!prev.default_venue_id &&
        (venues || []).some((v) => {
          if (v.id !== prev.default_venue_id) return false;
          if (!value) return false;
          if (v.client_id === value) return true;
          const shared = Array.isArray(v.shared_client_ids) ? v.shared_client_ids : [];
          if (shared.includes(value)) return true;
          if (v.copied_for_client_id === value) return true;
          return false;
        });
      return {
        ...prev,
        client_id: value || '',
        client_contact_id: '',
        default_venue_id: stillVisible ? prev.default_venue_id : '',
      };
    });
    setClientContacts([]); // Clear contacts list

    if (value) {
      loadClientContacts(value);
    }
  }, [loadClientContacts, venues]);

  // EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Server-side data fetching
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Build query params for server-side operations
      const params = {
        page,
        limit,
      };
      
      // Add sort
      if (sortField) {
        params.sort = `${sortField}:${sortDirection || 'desc'}`;
      }
      
      // Add search
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await getProjectsUnified(params);
      
      // Handle paginated response format { data: [...], meta: {...} }
      if (response.data?.data && response.data?.meta) {
        setProjects(response.data.data);
        setTotalItems(response.data.meta.total);
        setTotalPages(response.data.meta.total_pages);
      } else {
        // Fallback for non-paginated response
        setProjects(response.data || []);
        setTotalItems(response.data?.length || 0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Failed to load projects:", err);
      setError(true);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortField, sortDirection, searchTerm]);

  // Handle server-side sort
  const handleSort = useCallback((field, direction) => {
    setProjects([]); // Clear data immediately per Data Handling Rule #5
    setSortField(field);
    setSortDirection(direction || 'desc');
    setPage(1); // Reset to first page on sort change
  }, []);

  const getClientName = (clientId) => {
    // EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
    // Use the shared display policy so this page stays consistent with
    // the Dashboard. The list has the loaded `clients` array, so we
    // resolve via that map first; the helper falls back to the
    // explicit "Client linked — name missing" label for dangling refs
    // and "No client" only when client_id is genuinely absent.
    const clientsById = {};
    (clients || []).forEach((c) => { if (c && c.id) clientsById[c.id] = c; });
    return formatProjectClient({ client_id: clientId }, clientsById).label;
  };

  // EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: REMOVED client-side filtering/sorting
  // Data now comes directly from server via loadProjects()

  // Validate dates
  const validateDates = (startDate, endDate) => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        return "Start date must be before end date";
      }
    }
    return "";
  };

  const handleDateChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Validate dates
    const error = validateDates(
      field === "start_date" ? value : formData.start_date,
      field === "end_date" ? value : formData.end_date
    );
    setDateError(error);
  };

  const handleOpenDialog = (project = null) => {
    setDateError("");
    setClientContacts([]); // Reset contacts
    
    if (project) {
      setSelectedProject(project);
      setFormData({
        client_id: project.client_id,
        // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Include client_contact_id
        client_contact_id: project.client_contact_id || "",
        name: project.name,
        description: project.description || "",
        status: project.status,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        // New fields
        project_type: project.project_type || "order",
        quote_ref: project.quote_ref || "",
        po_number: project.po_number || "",
        cost_centre: project.cost_centre || "none",
        cost: project.cost || "",
        default_venue_id: project.default_venue_id || "",
        comments: project.comments || "",
        enable_gps_sign_in: project.enable_gps_sign_in || false,
      });
      
      // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Load contacts for existing client
      if (project.client_id) {
        loadClientContacts(project.client_id);
      }
    } else {
      setSelectedProject(null);
      setFormData({
        client_id: "",
        client_contact_id: "",
        name: "",
        description: "",
        status: "draft",
        start_date: "",
        end_date: "",
        // New fields
        project_type: "order",
        quote_ref: "",
        po_number: "",
        cost_centre: "none",
        cost: "",
        default_venue_id: "",
        comments: "",
        enable_gps_sign_in: false,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate dates before submitting
    const error = validateDates(formData.start_date, formData.end_date);
    if (error) {
      setDateError(error);
      toast.error(error);
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        default_venue_id: formData.default_venue_id || null,
        // EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Include client_contact_id
        client_contact_id: formData.client_contact_id || null,
      };
      if (selectedProject) {
        await updateProject(selectedProject.id, data);
        toast.success("Project updated successfully");
        setDialogOpen(false);
        loadProjects();
      } else {
        // EVENTSFLO-PROJECT-CREATE-NEXT-STEP-ADD-SHIFT-V1
        // After successful project creation, take the user straight
        // to the Add Shift form for the new project. If the create
        // response is missing an id for any reason, fall back to the
        // existing behaviour (stay on the list) instead of failing.
        const createRes = await createProject(data);
        toast.success("Project created — add the first shift");
        setDialogOpen(false);
        const newProjectId = createRes?.data?.id;
        if (newProjectId) {
          navigate(`/projects/${newProjectId}?addShift=true`);
        } else {
          loadProjects();
        }
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = async (project) => {
    setSelectedProject(project);
    setDeletionReason("");
    setDeletionImpact(null);
    setDeleteDialogOpen(true);
    
    // Fetch deletion impact
    try {
      const response = await getProjectDeletionImpact(project.id);
      setDeletionImpact(response.data);
    } catch (err) {
      console.error("Failed to get deletion impact:", err);
      // Still allow deletion even if impact fetch fails
      setDeletionImpact({ error: true, warning_message: "Unable to analyze dependencies." });
    }
  };

  const handleDelete = async () => {
    const reason = deletionReason?.trim() || "";
    
    if (reason.length < 10) {
      toast.error("Please provide a deletion reason (at least 10 characters)");
      return;
    }
    
    setDeleting(true);
    try {
      const result = await deleteProject(selectedProject.id, reason);
      toast.success(
        `Project deleted. Removed: ${result.data.shifts_deleted || 0} shifts, ${result.data.tasks_deleted || 0} tasks, ${result.data.assignments_deleted || 0} assignments.`
      );
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      setDeletionReason("");
      setDeletionImpact(null);
      loadProjects();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Columns with server-side sort support
  const columns = [
    { 
      key: "name", 
      label: "Name",
      sortable: true,
      sortKey: "name",
      render: (value, row) => (
        <button
          onClick={() => navigate(`/projects/${row.id}`)}
          className="text-teal-400 hover:text-teal-300 hover:underline font-medium text-left"
          data-testid={`project-name-link-${row.id}`}
        >
          {value}
        </button>
      ),
    },
    {
      key: "client_id",
      label: "Client",
      sortable: false, // Cannot sort by derived field without backend support
      render: (value) => getClientName(value),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortKey: "status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "start_date",
      label: "Start",
      sortable: true,
      sortKey: "start_date",
      render: (value) => formatDate(value),
    },
    {
      key: "end_date",
      label: "End",
      sortable: true,
      sortKey: "end_date",
      render: (value) => formatDate(value),
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      sortKey: "created_at",
      render: (value) => formatDate(value),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${row.id}`);
            }}
            title="View Details"
            data-testid={`view-project-${row.id}`}
          >
            <Eye className="h-4 w-4 text-teal-400" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            data-testid={`edit-project-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row);
            }}
            data-testid={`delete-project-${row.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load projects"
        message="We couldn't load the projects data. This might be a temporary connection issue. Please try again."
        onRetry={loadProjects}
      />
    );
  }

  return (
    <div data-testid="projects-page">
      {/* EF-GLOBAL-PAGE-ZOOM-FIX-V1: Wrap page content with zoom container */}
      <PageZoomContainer scale={scale}>
        <PageHeader
          title="Projects"
          subtitle={`Manage project containers for shifts${totalItems > 0 ? ` • ${totalItems} total` : ''}`}
          actions={
            <div className="flex items-center gap-2">
              {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
              <GuideButton testId="projects-guide-btn" />
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
                data-testid="add-project-btn"
                disabled={clients.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </Button>
            </div>
          }
        />

        {clients.length === 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 mb-6 text-amber-500 text-sm">
            You need to create a client before adding projects.
          </div>
        )}

        {/* EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Search Controls (sort is in table headers) */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="project-search-input"
            />
          </div>
          
          {/* EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom Controls */}
          <ZoomControl
            currentLabel={currentZoom.label}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            wrapText={wrapText}
            onToggleWrap={toggleWrap}
          />
        </div>

        <div className="bg-card border border-white/5 rounded-sm">
          {projects.length === 0 && !loading ? (
            <EmptyState
              icon={FolderKanban}
              title={searchTerm ? "No projects match your search" : "No projects yet"}
              description={searchTerm ? "Try a different search term." : "Projects are containers for shifts. Create one to start scheduling."}
              action={
                !searchTerm && clients.length > 0 && (
                  <Button
                    onClick={() => handleOpenDialog()}
                    className="bg-primary text-primary-foreground"
                    data-testid="empty-add-project-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Project
                  </Button>
                )
              }
            />
          ) : (
            <div 
              className={`${!wrapText ? '[&_td]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}
            >
              {/* EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Server-side sorting via onSort */}
              <DataTable 
                columns={columns} 
                data={projects}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                disableLocalSort={true}
              />
            </div>
          )}
        </div>
        
        {/* EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-2">
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages} ({totalItems} items)
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                data-testid="projects-prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                data-testid="projects-next-page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </PageZoomContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedProject ? "Edit Project" : "Add Project"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Type and Quote Ref */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project_type">Type *</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                >
                  <SelectTrigger data-testid="project-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order">Order</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="quote_ref">Quote Ref #</Label>
                <Input
                  id="quote_ref"
                  value={formData.quote_ref}
                  onChange={(e) => setFormData({ ...formData, quote_ref: e.target.value })}
                  placeholder="Quote reference"
                  data-testid="project-quote-ref-input"
                />
              </div>
            </div>

            {/* Row 2: Project Name */}
            <div>
              <Label htmlFor="name">Project Name *</Label>
              <ProjectNameAutocomplete
                id="name"
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                projects={projects}
                placeholder="Project name"
                required
                data-testid="project-name-input"
              />
            </div>

            {/* Row 3: PO Number and Cost Centre */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="po_number">PO Number</Label>
                <Input
                  id="po_number"
                  value={formData.po_number}
                  onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                  placeholder="Purchase order number"
                  data-testid="project-po-number-input"
                />
              </div>
              <div>
                <Label htmlFor="cost_centre">Cost Centre</Label>
                <Select
                  value={formData.cost_centre}
                  onValueChange={(value) => setFormData({ ...formData, cost_centre: value })}
                >
                  <SelectTrigger data-testid="project-cost-centre-select">
                    <SelectValue placeholder="Select cost centre" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Cost Centre</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="events">Events</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: Cost and Client */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cost">Cost</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  placeholder="0.00"
                  data-testid="project-cost-input"
                />
              </div>
              <div>
                <Label htmlFor="client_id">Client *</Label>
                <ClientSelect
                  clients={clients}
                  value={formData.client_id}
                  onChange={handleClientChange}
                  placeholder="Search or select client..."
                  testId="project-client-select"
                />
              </div>
            </div>

            {/* EF-PROJECT-CLIENT-CONTACT-INTEGRATION-V1: Client Contact Dropdown */}
            {formData.client_id && (
              <div>
                <Label htmlFor="client_contact_id">Client Contact (for Quotes/Invoices)</Label>
                <Select
                  value={formData.client_contact_id || "no-contact"}
                  onValueChange={(value) => setFormData({ ...formData, client_contact_id: value === "no-contact" ? "" : value })}
                  disabled={contactsLoading}
                >
                  <SelectTrigger data-testid="project-client-contact-select">
                    <SelectValue placeholder={contactsLoading ? "Loading contacts..." : "Select contact person..."} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-contact">No Contact Selected</SelectItem>
                    {clientContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.full_name || contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                        {(contact.mobile || contact.phone) && ` (${contact.mobile || contact.phone})`}
                      </SelectItem>
                    ))}
                    {clientContacts.length === 0 && !contactsLoading && (
                      <SelectItem value="no-contacts-available" disabled>
                        No contacts available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  This contact will be shown on quotes and invoices
                </p>
              </div>
            )}

            {/* Row 5: Default Venue */}
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="default_venue_id">Default Venue</Label>
                {/* EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1 */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setQuickAddVenueOpen(true)}
                  data-testid="project-add-venue-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Venue
                </Button>
              </div>
              <Select
                value={formData.default_venue_id || "no-venue"}
                onValueChange={(value) => setFormData({ ...formData, default_venue_id: value === "no-venue" ? "" : value })}
                disabled={!formData.client_id}
              >
                <SelectTrigger data-testid="project-venue-select">
                  <SelectValue
                    placeholder={
                      formData.client_id
                        ? "Select venue"
                        : "Select a Client before choosing a Venue"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-venue">No Default Venue</SelectItem>
                  {/* EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
                      Only venues associated with the selected client
                      (owned / shared / client-copy) appear. */}
                  {filterVenuesForClient(venues, formData.client_id).map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 6: Comments */}
            <div>
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Additional comments"
                rows={3}
                data-testid="project-comments-input"
              />
            </div>

            {/* Row 7: Status and Dates */}
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <DatePicker
                  value={formData.start_date}
                  onChange={(value) => handleDateChange("start_date", value)}
                  placeholder="Select start date"
                  className={dateError ? "border-red-500" : ""}
                  data-testid="project-start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <DatePicker
                  value={formData.end_date}
                  onChange={(value) => handleDateChange("end_date", value)}
                  placeholder="Select end date"
                  minDate={formData.start_date}
                  className={dateError ? "border-red-500" : ""}
                  data-testid="project-end-date-input"
                />
              </div>
            </div>
            {dateError && (
              <p className="text-sm text-red-500 mt-1" data-testid="project-date-error">
                {dateError}
              </p>
            )}

            {/* Row 8: GPS Sign In/Out */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="enable_gps_sign_in"
                checked={formData.enable_gps_sign_in}
                onCheckedChange={(checked) => setFormData({ ...formData, enable_gps_sign_in: checked })}
                data-testid="project-gps-checkbox"
              />
              <Label htmlFor="enable_gps_sign_in" className="cursor-pointer">
                Enable GPS Sign In/Out
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="project-create-submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedProject ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1
          Nested QuickAddVenueDialog opens on top of the Project Dialog.
          After successful create we refresh venues, auto-select the new
          one on the project form, and keep the parent dialog open. */}
      <QuickAddVenueDialog
        open={quickAddVenueOpen}
        onOpenChange={setQuickAddVenueOpen}
        clientId={formData.client_id || null}
        onCreated={async (venue) => {
          try {
            const res = await getVenues();
            const list = Array.isArray(res) ? res : (res?.data ?? []);
            setVenues(list);
          } catch (_) { /* non-fatal */ }
          setFormData((prev) => ({ ...prev, default_venue_id: venue.id }));
        }}
      />

      {/* Governed Delete Confirmation Modal */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeletionImpact(null);
          setDeletionReason("");
        }
      }}>
        <DialogContent className="bg-surface-base border border-border sm:max-w-[500px]" data-testid="project-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Delete Project
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Project Name */}
            <p className="text-foreground">
              You are about to delete project: <span className="font-semibold text-white">{selectedProject?.name}</span>
            </p>
            
            {/* Deletion Impact */}
            <div className="p-3 bg-surface-elevated/50 rounded-lg border border-border" data-testid="project-deletion-impact">
              <h4 className="text-sm font-medium text-amber-400 mb-2">Deletion Impact</h4>
              {!deletionImpact ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing dependencies...
                </p>
              ) : deletionImpact.error ? (
                <p className="text-sm text-muted-foreground">{deletionImpact.warning_message}</p>
              ) : deletionImpact.has_dependencies ? (
                <div className="space-y-2">
                  <p className="text-sm text-foreground">{deletionImpact.warning_message}</p>
                  <div className="text-sm space-y-1">
                    {deletionImpact.shift_count > 0 && (
                      <p className="text-muted-foreground">
                        Shifts: <span className="text-white">{deletionImpact.shift_count}</span>
                      </p>
                    )}
                    {deletionImpact.total_tasks > 0 && (
                      <p className="text-muted-foreground">
                        Tasks: <span className="text-white">{deletionImpact.total_tasks}</span>
                        {deletionImpact.rule_added_tasks > 0 && (
                          <span className="text-teal-400 ml-1">({deletionImpact.rule_added_tasks} rule-added)</span>
                        )}
                      </p>
                    )}
                    {deletionImpact.assignment_count > 0 && (
                      <p className="text-muted-foreground">
                        Crew assignments: <span className="text-white">{deletionImpact.assignment_count}</span>
                      </p>
                    )}
                    {deletionImpact.financial_impact > 0 && (
                      <p className="text-muted-foreground">
                        Financial impact: <span className="text-white">{formatCurrency(deletionImpact.financial_impact)}</span>
                      </p>
                    )}
                    {(deletionImpact.quotes_affected > 0 || deletionImpact.invoices_affected > 0) && (
                      <p className="text-muted-foreground">
                        Documents: {deletionImpact.quotes_affected > 0 && <span className="text-white">{deletionImpact.quotes_affected} quote(s)</span>}
                        {deletionImpact.quotes_affected > 0 && deletionImpact.invoices_affected > 0 && <span className="text-muted-foreground">, </span>}
                        {deletionImpact.invoices_affected > 0 && <span className="text-white">{deletionImpact.invoices_affected} invoice(s)</span>}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-green-400">No dependencies found. Safe to delete.</p>
              )}
            </div>
            
            {/* Deletion Reason Input */}
            <div>
              <Label htmlFor="deletion-reason" className="text-foreground">
                Reason for deletion <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="deletion-reason"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                placeholder="Please explain why this project is being deleted (minimum 10 characters)"
                className="mt-1 bg-surface-elevated border-border text-foreground"
                data-testid="project-deletion-reason-input"
              />
              {(deletionReason?.trim() || "").length > 0 && (deletionReason?.trim() || "").length < 10 && (
                <p className="text-xs text-amber-400 mt-1">{10 - (deletionReason?.trim() || "").length} more characters required</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletionImpact(null);
                setDeletionReason("");
              }}
              disabled={deleting}
              data-testid="project-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || (deletionReason?.trim() || "").length < 10}
              className="bg-red-600 hover:bg-red-700"
              data-testid="project-delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Project"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
